import { useState, useEffect } from 'react';
import { apiFetch, apiPost, apiPut } from '../api/client';
import { PageContainer } from '../components/PageContainer';
import type { ImageSource } from '@posternauts/shared';

interface DiscoveryFormState {
  sourceSlug: string;
  query: string;
  maxResults: number;
  yearStart: string;
  yearEnd: string;
}

interface DiscoveryResponse {
  success: boolean;
  jobId?: string;
  message?: string;
  error?: string;
}

interface KeywordLists {
  include: string[];
  exclude: string[];
}

const docStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#fff',
  lineHeight: 1.55,
  margin: '0.4rem 0 0',
};

const infoBoxStyle: React.CSSProperties = {
  background: '#f0f4ff',
  border: '1px solid #c8d4f0',
  borderRadius: 6,
  padding: '0.75rem 1rem',
  marginTop: '0.75rem',
  fontSize: 13,
  color: '#000',
  lineHeight: 1.6,
};

const gateRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'baseline',
  marginTop: '0.35rem',
};

const gateLabelStyle: React.CSSProperties = {
  fontWeight: 600,
  minWidth: 60,
  color: '#1a3a6a',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

export default function SettingsPage() {
  const [form, setForm] = useState<DiscoveryFormState>({
    sourceSlug: 'nasa',
    query:      'nebula',
    maxResults: 10000,
    yearStart:  '',
    yearEnd:    '',
  });

  const [sources, setSources]         = useState<ImageSource[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [response, setResponse] = useState<DiscoveryResponse | null>(null);

  const [lists, setLists] = useState<KeywordLists>({ include: [], exclude: [] });
  const [keywordsLoading, setKeywordsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [newInclude, setNewInclude] = useState('');
  const [newExclude, setNewExclude] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleRun = async () => {
    setStatus('loading');
    setResponse(null);

    try {
      const body: Record<string, unknown> = {
        source:     form.sourceSlug,
        query:      form.query,
        maxResults: Number(form.maxResults),
      };
      if (form.yearStart) body['yearStart'] = Number(form.yearStart);
      if (form.yearEnd)   body['yearEnd']   = Number(form.yearEnd);

      const data = await apiPost<DiscoveryResponse>('/discovery/run', body);
      setResponse(data);
      setStatus(data.success ? 'success' : 'error');
    } catch (err) {
      setResponse({ success: false, error: 'Network error — check console' });
      setStatus('error');
    }
  };

  // Load active image sources for the source selector
  useEffect(() => {
    apiFetch<ImageSource[]>('/image-sources?activeOnly=true')
      .then(data => {
        setSources(data);
        // If current sourceSlug is no longer active, reset to first available
        if (data.length > 0 && !data.find(s => s.slug === form.sourceSlug)) {
          setForm(prev => ({ ...prev, sourceSlug: data[0]!.slug }));
        }
        setSourcesLoading(false);
      })
      .catch(() => setSourcesLoading(false));
  }, []);

  useEffect(() => {
    apiFetch<KeywordLists>('/settings/keywords')
      .then(data => { setLists(data); setKeywordsLoading(false); })
      .catch(() => setKeywordsLoading(false));
  }, []);

  const saveKeywords = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await apiPut('/settings/keywords', lists);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const removeKeyword = (type: 'include' | 'exclude', term: string) => {
    setLists(prev => ({ ...prev, [type]: prev[type].filter(t => t !== term) }));
    setSaveStatus('idle');
  };

  const addKeyword = (type: 'include' | 'exclude') => {
    const value = (type === 'include' ? newInclude : newExclude).trim();
    if (!value || lists[type].includes(value)) return;
    setLists(prev => ({ ...prev, [type]: [...prev[type], value] }));
    type === 'include' ? setNewInclude('') : setNewExclude('');
    setSaveStatus('idle');
  };

  const selectedSource = sources.find(s => s.slug === form.sourceSlug) ?? null;

  return (
    <PageContainer>
      <div style={{ padding: '2rem' }}>
        <h2>Discovery</h2>
        <p style={{ ...docStyle, marginTop: '0.5rem' }}>
          This page controls how new product candidates are found and filtered.
          Configure your search parameters and keyword filters, then run a discovery
          job to populate the Review queue with images that pass all three gates.
        </p>

        {/* ── Run Discovery ─────────────────────────────── */}
        <section style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: 0 }}>Run Discovery</h3>
          <p style={docStyle}>
            Kicks off a background job that searches the selected image archive using the
            parameters below. Each result is passed through the three-gate pipeline
            before landing in the Review queue. Already-seen images (approved,
            rejected, or previously evaluated) are automatically skipped.
          </p>

          <div style={infoBoxStyle}>
            <div style={gateRowStyle}>
              <span style={gateLabelStyle}>Gate 1</span>
              <span>Dimension check — image must meet minimum size requirements.</span>
            </div>
            <div style={gateRowStyle}>
              <span style={gateLabelStyle}>Gate 2</span>
              <span>Subject filter — title/description must match an <em>Include</em> keyword and must not match any <em>Exclude</em> keyword.</span>
            </div>
            <div style={gateRowStyle}>
              <span style={gateLabelStyle}>Gate 3</span>
              <span>AI aesthetic scoring — Claude Vision evaluates composition, color, and print suitability. Images scoring below the threshold are rejected.</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.25rem', maxWidth: '480px' }}>

            <label>
              Image Source
              <p style={{ ...docStyle, marginBottom: '0.25rem' }}>
                The archive to search. Only active sources appear here. Manage sources
                on the <a href="/sources" style={{ color: '#a0a8ff' }}>Sources</a> page.
              </p>
              {sourcesLoading ? (
                <div style={{ color: '#555', fontSize: 13, marginTop: '0.25rem' }}>Loading sources…</div>
              ) : sources.length === 0 ? (
                <div style={{ color: '#f87171', fontSize: 13, marginTop: '0.25rem' }}>
                  No active sources. Enable a source on the{' '}
                  <a href="/sources" style={{ color: '#a0a8ff' }}>Sources</a> page.
                </div>
              ) : (
                <select
                  value={form.sourceSlug}
                  onChange={e => setForm(prev => ({ ...prev, sourceSlug: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: '0.25rem', padding: '0.375rem 0.5rem', cursor: 'pointer' }}
                >
                  {sources.map(s => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              )}
              {selectedSource && (
                <div style={{ marginTop: '0.4rem', fontSize: 12, color: '#666' }}>
                  {selectedSource.licenseType.replace(/_/g, ' ')}
                  {selectedSource.attributionRequired && ' · Attribution required'}
                  {selectedSource.notes && (
                    <span title={selectedSource.notes}> · {selectedSource.notes.length > 60 ? selectedSource.notes.slice(0, 60) + '…' : selectedSource.notes}</span>
                  )}
                </div>
              )}
            </label>

            <label>
              Query
              <p style={{ ...docStyle, marginBottom: '0.25rem' }}>
                Sent directly to the archive's search API. Use broad astronomical terms
                (e.g. <em>nebula</em>, <em>galaxy</em>, <em>supernova</em>) to maximize
                candidate volume; Gate 2 keyword filtering handles the precision.
              </p>
              <input
                name="query"
                value={form.query}
                onChange={handleChange}
                style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
              />
            </label>

            <label>
              Max Results
              <p style={{ ...docStyle, marginBottom: '0.25rem' }}>
                Upper limit on how many results to fetch before filtering.
                Higher values are slower but reduce the chance of missing good candidates.
              </p>
              <input
                name="maxResults"
                type="number"
                value={form.maxResults}
                onChange={handleChange}
                style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
              />
            </label>

            <label>
              Year Start (optional)
              <input
                name="yearStart"
                type="number"
                value={form.yearStart}
                onChange={handleChange}
                placeholder="e.g. 2020"
                style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
              />
            </label>

            <label>
              Year End (optional)
              <input
                name="yearEnd"
                type="number"
                value={form.yearEnd}
                onChange={handleChange}
                placeholder="e.g. 2024"
                style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
              />
            </label>

            <button
              onClick={handleRun}
              disabled={status === 'loading' || sources.length === 0}
              style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              {status === 'loading' ? 'Enqueueing…' : 'Run Discovery'}
            </button>
          </div>

          {response && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: status === 'success' ? '#e6f4ea' : '#fce8e6',
              borderRadius: '4px',
            }}>
              {status === 'success'
                ? `✓ Enqueued — job ID: ${response.jobId}`
                : `✗ Error: ${response.error}`
              }
            </div>
          )}
        </section>

        {/* ── Subject Filter Keywords ────────────────────── */}
        <section style={{ marginTop: '2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Subject Filter Keywords</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {saveStatus === 'saved' && <span style={{ color: 'green', fontSize: 13 }}>Saved</span>}
              {saveStatus === 'error' && <span style={{ color: 'red', fontSize: 13 }}>Error saving</span>}
              <button onClick={saveKeywords} disabled={saving} style={{ cursor: 'pointer' }}>
                {saving ? 'Saving…' : 'Save Keywords'}
              </button>
            </div>
          </div>
          <p style={{ ...docStyle, marginTop: '0.5rem' }}>
            These lists drive Gate 2. Each image's title and description are checked
            against both lists. An image passes only if it matches <strong>at least one</strong> Include
            term and <strong>zero</strong> Exclude terms. Keywords are case-insensitive
            and matched as substrings, so <em>galaxy</em> will also match <em>galaxies</em>.
          </p>
          <p style={{ ...docStyle, marginTop: '0.35rem' }}>
            Changes here take effect on the next discovery run — they do not
            retroactively re-evaluate previously rejected candidates.
          </p>

          {keywordsLoading ? <p>Loading…</p> : (
            <>
              <KeywordList
                label="Include"
                description="Images must match at least one of these terms to pass Gate 2."
                terms={lists.include}
                newValue={newInclude}
                onNewValueChange={setNewInclude}
                onAdd={() => addKeyword('include')}
                onRemove={t => removeKeyword('include', t)}
                chipColor="#1a4a8a"
              />
              <KeywordList
                label="Exclude"
                description="Images matching any of these terms are discarded, even if they match an Include keyword."
                terms={lists.exclude}
                newValue={newExclude}
                onNewValueChange={setNewExclude}
                onAdd={() => addKeyword('exclude')}
                onRemove={t => removeKeyword('exclude', t)}
                chipColor="#7a1a1a"
              />
            </>
          )}
        </section>
      </div>
    </PageContainer>
  );
}

interface KeywordListProps {
  label: string;
  description: string;
  terms: string[];
  newValue: string;
  onNewValueChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (term: string) => void;
  chipColor: string;
}

function KeywordList({
                       label, description, terms, newValue, onNewValueChange, onAdd, onRemove, chipColor,
                     }: KeywordListProps) {
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <h4 style={{ marginBottom: '0.25rem' }}>{label}</h4>
      <p style={{ color: '#fff', fontSize: 13, marginBottom: '0.5rem' }}>{description}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {terms.map(term => (
          <span key={term} style={{ ...chipStyle, background: chipColor }}>
            {term}
            <button
              onClick={() => onRemove(term)}
              style={chipDeleteStyle}
              title={`Remove "${term}"`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', maxWidth: '480px' }}>
        <input
          value={newValue}
          onChange={e => onNewValueChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onAdd()}
          placeholder={`Add ${label.toLowerCase()} keyword…`}
          style={{ flex: 1, padding: '0.375rem 0.5rem' }}
        />
        <button onClick={onAdd} style={{ cursor: 'pointer', padding: '0.375rem 0.75rem' }}>
          Add
        </button>
      </div>
    </div>
  );
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
  padding: '3px 10px', borderRadius: 20, color: '#fff', fontSize: 13,
};

const chipDeleteStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#fff',
  cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0,
};